/* eslint no-alert: 0 */
import cornerstoneTools from 'cornerstone-tools';

const BaseAnnotationTool = cornerstoneTools.importInternal('base/BaseAnnotationTool');

const EVENTS = cornerstoneTools.EVENTS;
//const EVENTS = cornerstoneTools.importInternal('events');
const moveNewHandle = cornerstoneTools.import(
    'manipulators/moveNewHandle',
)

const pointInsideBoundingBox = cornerstoneTools.importInternal('util/pointInsideBoundingBox');
const lineSegDistance = cornerstoneTools.import('util/lineSegDistance');
const triggerEvent = cornerstoneTools.importInternal('util/triggerEvent');
const drawLinkedTextBox = cornerstoneTools.importInternal('drawing/drawLinkedTextBox');
const drawArrow = cornerstoneTools.importInternal('drawing/drawArrow');
const getNewContext = cornerstoneTools.import('drawing/getNewContext');
const draw = cornerstoneTools.import('drawing/draw');
const setShadow = cornerstoneTools.import('drawing/setShadow');
const drawHandles = cornerstoneTools.import('drawing/drawHandles')

const arrowAnnotateCursor = cornerstoneTools.importInternal('cursors/index');

export function textBoxWidth(context, text, padding) {
    const font = cornerstoneTools.textStyle.getFont();
    const origFont = context.font;

    if (font && font !== origFont) {
        context.font = font;
    }
    const width = context.measureText(text).width;

    if (font && font !== origFont) {
        context.font = origFont;
    }

    return width + 2 * padding;
}


/**
 * @public
 * @class ArrowAnnotateTool
 * @memberof Tools.Annotation
 * @classdesc Create and position an arrow and label
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class ArrowAnnotateToolEyonix extends BaseAnnotationTool {
    constructor(props = {}) {
        const defaultProps = {
            name: 'ArrowAnnotate',
            supportedInteractionTypes: ['Mouse', 'Touch'],
            configuration: {
                getTextCallback: getTextCallback,
                changeTextCallback: changeTextCallback,
                drawHandles: true,
                drawHandlesOnHover: false,
                hideHandlesIfMoving: false,
                arrowFirst: true,
                renderDashed: false,
                allowEmptyLabel: false,
            },
            svgCursor: arrowAnnotateCursor,
        };

        super(props, defaultProps);
        this.preventNewMeasurement = false;
    }

    createNewMeasurement(evt) {
        // Create the measurement data for this tool with the end handle activated
        return {
            visible: true,
            active: true,
            color: undefined,
            fontSize: undefined,
            textVisible: undefined,
            who: undefined,
            when: undefined,
            whenStr: undefined,
            invalidated: true,
            handles: {
                start: {
                    x: evt.detail.currentPoints.image.x,
                    y: evt.detail.currentPoints.image.y,
                    highlight: true,
                    active: false,
                },
                end: {
                    x: evt.detail.currentPoints.image.x,
                    y: evt.detail.currentPoints.image.y,
                    highlight: true,
                    active: false,
                },
                textBox: {
                    active: true,
                    hasMoved: false,
                    movesIndependently: false,
                    drawnIndependently: true,
                    allowedOutsideImage: true,
                    hasBoundingBox: true,
                },
            },
        };
    }

    pointNearTool(element, data, coords) {
        if (data.visible === false) {
            return false;
        }

        return (
            lineSegDistance(element, data.handles.start, data.handles.end, coords) <
            25
        );
    }

    updateCachedStats() {
        // Implementing to satisfy BaseAnnotationTool
    }

    renderToolData(evt) {
        const { element, enabledElement } = evt.detail;
        const {
            handleRadius,
            drawHandlesOnHover,
            hideHandlesIfMoving,
            renderDashed,
        } = this.configuration;

        // If we have no toolData for this element, return immediately as there is nothing to do
        const toolData = cornerstoneTools.getToolState(element, this.name);

        if (!toolData) {
            return;
        }

        // We have tool data for this element - iterate over each one and draw it
        const canvas = evt.detail.canvasContext.canvas;
        const context = getNewContext(canvas);

        const lineWidth = cornerstoneTools.toolStyle.getToolWidth();
       // const hideHandlers = evt.detail.viewport.hideHanders || false;

        let lineDash;

        if (renderDashed) {
            lineDash = cornerstoneTools.getModule('globalConfiguration').configuration.lineDash;
        }

        for (let i = 0; i < toolData.data.length; i++) {
            const data = toolData.data[i];

            if (data.visible === false) {
                continue;
            }

            draw(context, context => {
                setShadow(context, this.configuration);


                const color = cornerstoneTools.toolColors.getColorIfActive(data);
                data.color = data.color || cornerstoneTools.toolColors.getToolColor(data);

                let initFontSize = cornerstoneTools.textStyle.getFontSize();

                data.fontSize = data.fontSize !== undefined? data.fontSize : cornerstoneTools.textStyle.getFontSize();
                data.textVisible = data.textVisible !== undefined? data.textVisible : this.options.annotationTextVisible;

                cornerstoneTools.textStyle.setFont(`${data.fontSize}px Roboto`);
                cornerstoneTools.textStyle.setFontSize(data.fontSize);


                if(data.invalidated && (data.who !== this.initialConfiguration.user || data.when !== new Date().toLocaleString())) {
                    data.who = this.initialConfiguration.user;
                    data.when = new Date().toLocaleString();
                    if(this.initialConfiguration.momentLib && this.initialConfiguration.defaultDateTimeFormat) {
                        data.whenStr = this.initialConfiguration.momentLib().format(this.initialConfiguration.defaultDateTimeFormat);
                    } else {
                        data.whenStr = new Date().toLocaleString();
                    }
                }

                // Draw the arrow
                const handleStartCanvas = window.cornerstone.pixelToCanvas(
                    element,
                    data.handles.start
                );
                const handleEndCanvas = window.cornerstone.pixelToCanvas(
                    element,
                    data.handles.end
                );


                // Config.arrowFirst = false;
                if (this.configuration.arrowFirst) {
                    drawArrow(
                        context,
                        handleEndCanvas,
                        handleStartCanvas,
                        color,
                        lineWidth,
                        lineDash
                    );
                } else {
                    drawArrow(
                        context,
                        handleStartCanvas,
                        handleEndCanvas,
                        color,
                        lineWidth,
                        lineDash
                    );
                }

                const handleOptions = {
                    color,
                    handleRadius,
                    drawHandlesIfActive: drawHandlesOnHover,
                    hideHandlesIfMoving,
                };

                if (data.active) {
                    drawHandles(context, evt.detail, data.handles, handleOptions);
                }

                const text = textBoxText(data);

                const tooltipText =  `${data.who} (${data.whenStr})` ;


                if((data.active || this.options.showUser) && data.who) {
                    drawLinkedTextBox(
                        context,
                        element,
                        {
                            x: data.handles.end.x - (data.handles.end.x - data.handles.start.x)/2,
                            y: data.handles.end.y - (data.handles.end.y - data.handles.start.y)/2,
                        },
                        tooltipText,
                        data.handles,
                        textBoxAnchorPoints,
                        "white",
                        lineWidth,
                        0,
                        false
                    );
                }

                data.invalidated = false;

                // Draw the text
                if (text && text !== '') {

                    // Calculate the text coordinates.
                    const padding = 5;
                    const textWidth = textBoxWidth(context, text, padding);
                    const textHeight = cornerstoneTools.textStyle.getFontSize() + 10;


                    let distance = Math.max(textWidth, textHeight) / 2 + 5;

                    if (handleEndCanvas.x < handleStartCanvas.x) {
                        distance = -distance;
                    }

                    if (!data.handles.textBox.hasMoved) {
                        let textCoords;


                        if (this.configuration.arrowFirst) {
                            textCoords = {
                                x: handleEndCanvas.x - textWidth / 2 + distance,
                                y: handleEndCanvas.y - textHeight / 2,
                            };
                        } else {
                            // If the arrow is at the End position, the text should
                            // Be placed near the Start position
                            textCoords = {
                                x: handleStartCanvas.x - textWidth / 2 - distance,
                                y: handleStartCanvas.y - textHeight / 2,
                            };
                        }

                        const transform = window.cornerstone.internal.getTransform(
                            enabledElement
                        );

                        transform.invert();

                        const coords = transform.transformPoint(textCoords.x, textCoords.y);

                        data.handles.textBox.x = coords.x;
                        data.handles.textBox.y = coords.y;
                   }

                    drawLinkedTextBox(
                        context,
                        element,
                        data.handles.textBox,
                        text,
                        data.handles,
                        textBoxAnchorPoints,
                        color,
                        lineWidth,
                        0,
                        false
                    );
                }
                cornerstoneTools.textStyle.setFontSize(initFontSize);
                cornerstoneTools.textStyle.setFont(`${initFontSize}px Roboto`);
            });
        }

        function textBoxText(data) {
            return data.text;
        }

        function textBoxAnchorPoints(handles) {
            const midpoint = {
                x: (handles.start.x + handles.end.x) / 2,
                y: (handles.start.y + handles.end.y) / 2,
            };

            return [handles.start, midpoint, handles.end];
        }
    }

    addNewMeasurement(evt, interactionType) {
        const element = evt.detail.element;
        const measurementData = this.createNewMeasurement(evt);

        const { allowEmptyLabel } = this.configuration;

        // Associate this data with this imageId so we can render it and manipulate it
        cornerstoneTools.addToolState(element, this.name, measurementData);
        window.cornerstone.updateImage(element);

        moveNewHandle(
            evt.detail,
            this.name,
            measurementData,
            measurementData.handles.end,
            this.options,
            interactionType,
            success => {
                if (success) {
                    if (measurementData.text === undefined) {
                        this.configuration.getTextCallback(text => {
                            if (text || allowEmptyLabel) {
                                measurementData.text = text;
                                measurementData.active = false;

                                const modifiedEventData = {
                                    toolName: this.name,
                                    toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
                                    element,
                                    measurementData,
                                };

                                window.cornerstone.updateImage(element);
                                triggerEvent(
                                    element,
                                    EVENTS.MEASUREMENT_COMPLETED,
                                    modifiedEventData
                                );
                            } else {
                                cornerstoneTools.removeToolState(element, this.name, measurementData);
                            }
                        }, evt.detail);
                    }
                } else {
                    cornerstoneTools.removeToolState(element, this.name, measurementData);
                }

                window.cornerstone.updateImage(element);
            }
        );
    }

    doubleClickCallback(evt) {
        return this._updateTextForNearbyAnnotation(evt);
    }

    touchPressCallback(evt) {
        return this._updateTextForNearbyAnnotation(evt);
    }

    _updateTextForNearbyAnnotation(evt) {
        const element = evt.detail.element;
        const coords = evt.detail.currentPoints.canvas;
        const toolState = cornerstoneTools.getToolState(element, this.name);

        if (!toolState) {
            return false;
        }

        for (let i = 0; i < toolState.data.length; i++) {
            const data = toolState.data[i];

            if (
                this.pointNearTool(element, data, coords) ||
                pointInsideBoundingBox(data.handles.textBox, coords)
            ) {
                data.active = true;
                window.cornerstone.updateImage(element);

                // Allow relabelling via a callback
                this.configuration.changeTextCallback(
                    data,
                    evt.detail,
                    this._doneChangingTextCallback.bind(this, element, data)
                );

                evt.stopImmediatePropagation();
                evt.preventDefault();
                evt.stopPropagation();

                return true;
            }
        }
    }

    _doneChangingTextCallback(element, measurementData, updatedText, deleteTool) {
        if (deleteTool === true) {
            cornerstoneTools.removeToolState(element, this.name, measurementData);
        } else {
            measurementData.text = updatedText;
        }

        measurementData.active = false;
        window.cornerstone.updateImage(element);

        triggerEvent(element, EVENTS.MEASUREMENT_MODIFIED, {
            toolName: this.name,
            toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
            element,
            measurementData,
        });
    }
}


function getTextCallback(doneChangingTextCallback) {
    const customDoneChangingTextCallback = () => {
        doneChangingTextCallback(document.getElementById("annotation-text").value);

        document.getElementById("text-tool-modal").style.display = "none";

        document.getElementById("save-annotation").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation").removeEventListener("click", closeModal);
    };

    const closeModal = () => {
        document.getElementById("text-tool-modal").style.display = "none";

        document.getElementById("save-annotation").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation").removeEventListener("click", closeModal);
    };


        document.getElementById("annotation-text").value = "";

    document.getElementById("text-tool-modal").style.display = "block";

    document.getElementById("save-annotation").addEventListener("click", customDoneChangingTextCallback);
    document.getElementById("cancel-annotation").addEventListener("click", closeModal);

}

function changeTextCallback(data, eventData, doneChangingTextCallback) {
        const customDoneChangingTextCallback = () => {
            doneChangingTextCallback(document.getElementById("annotation-text").value);

            document.getElementById("text-tool-modal").style.display = "none";

            document.getElementById("save-annotation").removeEventListener("click", customDoneChangingTextCallback);
            document.getElementById("save-annotation").removeEventListener("click", closeModal);
        };

        const closeModal = () => {
            document.getElementById("text-tool-modal").style.display = "none";

            document.getElementById("save-annotation").removeEventListener("click", customDoneChangingTextCallback);
            document.getElementById("save-annotation").removeEventListener("click", closeModal);
        };

        if (data.text !== 'double click to edit text')
            document.getElementById("annotation-text").value = data.text;
        else
            document.getElementById("annotation-text").value = "";

        document.getElementById("text-tool-modal").style.display = "block";

        document.getElementById("save-annotation").addEventListener("click", customDoneChangingTextCallback);
        document.getElementById("cancel-annotation").addEventListener("click", closeModal);

}